/**
 * MarketResearchPromo — landing-page banner promoting the user-research
 * survey in exchange for 1 free year of VIP (ad-free) access.
 *
 * Compact-by-default layout (May 12 redesign):
 *   - Only a one-line teaser is visible by default — chevron expands
 *     the body + sign-up CTA. Saves vertical space on the landing page
 *     so the LinktreeHub starts higher.
 *   - Hard-coded bilingual copy (English + Spanish) so the toggle works
 *     immediately. Backend config (headline/body/cta_label) is still
 *     used for the English copy when present.
 *   - Removed the "Download flyer" button — it's now admin-only inside
 *     the Promo Settings card. Public flyer links are not needed.
 *   - Sign-up CTA goes directly to the Google Form. The raw URL line
 *     under the buttons (previously shown for copy-paste) is also gone.
 *   - NOT dismissible (June 2026): the X button let users permanently
 *     hide the promo and it never came back. Now the banner is only
 *     minimizable via the chevron — it always reappears on page load.
 */
import React, { useState, useEffect } from 'react';
import { Sparkles, ArrowUpRight, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback bilingual copy. The English values can be overridden by
// the admin via `GET /api/promo/market-research`; Spanish always
// comes from here so we don't need a backend round-trip to translate.
const COPY = {
  badge: { en: 'Research Promo', es: 'Promo de Investigación' },
  headline: { en: '1 Year Free VIP', es: '1 año gratis de VIP' },
  subhead: { en: 'VIP · Ad-Free · Everything', es: 'VIP · Sin anuncios · Todo' },
  body: {
    en: 'Help shape ClearCommunicationApp. Opt in to occasional online surveys about how you use the app and we\u2019ll thank you with a full year of VIP access.',
    es: 'Ayuda a dar forma a ClearCommunicationApp. Apúntate a encuestas ocasionales en línea sobre cómo usas la app y te lo agradeceremos con un año completo de acceso VIP.',
  },
  ctaLabel: { en: 'Sign up free', es: 'Regístrate gratis' },
  expandLabel: { en: 'Learn more', es: 'Más información' },
  collapseLabel: { en: 'Hide details', es: 'Ocultar detalles' },
};

export function MarketResearchPromo() {
  const { isSpanish } = useLanguage();
  const [config, setConfig] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/promo/market-research`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => { if (!cancelled && data?.enabled) setConfig(data); })
      .catch(() => { /* offline or 5xx — banner stays hidden, no big deal */ });
    return () => { cancelled = true; };
  }, []);

  if (!config) return null;

  const lang = isSpanish ? 'es' : 'en';
  // Allow admin-supplied English copy to override defaults; ES is always local.
  const headline = isSpanish ? COPY.headline.es : (config.headline || COPY.headline.en);
  const subhead  = isSpanish ? COPY.subhead.es  : (config.subhead  || COPY.subhead.en);
  const body     = isSpanish ? COPY.body.es     : (config.body     || COPY.body.en);
  const ctaLabel = isSpanish ? COPY.ctaLabel.es : (config.cta_label|| COPY.ctaLabel.en);

  const trackClick = (where) => {
    try {
      window.gtag?.('event', 'market_research_promo_click', {
        event_category: 'engagement',
        event_label: where,
      });
    } catch (_) { /* analytics may not be loaded */ }
  };

  return (
    <section
      data-testid="market-research-promo"
      className="relative mx-auto mt-3 mb-1 max-w-md overflow-hidden rounded-2xl border border-[#00E5CC]/40 shadow-md
                 bg-white dark:bg-[#0d1f2d]
                 text-[#0d1f2d] dark:text-[#0d1f2d]"
    >
      {/* Compact teaser header — always visible. Clicking it toggles
          the expanded section so we only show one line by default. */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="market-research-promo-body"
        data-testid="market-research-promo-toggle"
        className="w-full text-left bg-[#00E5CC] px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-[#00D9C0] transition-colors"
      >
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#0d1f2d]/80">
            {COPY.badge[lang]}
          </p>
          <p className="text-base font-extrabold leading-tight text-[#0d1f2d] truncate">
            {headline} · <span className="font-semibold">{subhead}</span>
          </p>
        </div>
        {expanded
          ? <ChevronUp className="h-5 w-5 shrink-0 text-[#0d1f2d]" aria-hidden="true" />
          : <ChevronDown className="h-5 w-5 shrink-0 text-[#0d1f2d]" aria-hidden="true" />}
      </button>

      {/* Expanded body — only renders when toggled open. CSS-only
          transition would be nicer but conditional render is simpler
          and avoids layout jank on slower devices. */}
      {expanded && (
        <div id="market-research-promo-body" className="px-5 py-4 space-y-3 animate-in fade-in duration-200">
          <div className="flex items-start gap-2">
            <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#00A896] dark:text-[#00E5CC]" />
            <p className="text-sm leading-relaxed text-[#1f2937] dark:text-white/90">
              {body}
            </p>
          </div>

          <Button
            asChild
            size="sm"
            className="w-full bg-[#00E5CC] text-[#0d1f2d] hover:bg-[#00C9B3] font-semibold"
            data-testid="market-research-promo-cta"
          >
            <a
              href={config.form_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackClick('cta')}
            >
              {ctaLabel}
              <ArrowUpRight className="ml-1 h-3.5 w-3.5" />
            </a>
          </Button>
        </div>
      )}
    </section>
  );
}

export default MarketResearchPromo;
